import React, { useState } from 'react';
import './Calendar.css';

const Calendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    // Fill remaining cells to always show 6 weeks (42 days total)
    // This prevents the calendar from jumping when switching months
    const totalCells = 42; // 6 weeks * 7 days
    while (days.length < totalCells) {
      days.push(null);
    }
    
    return days;
  };
  
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const jumpToToday = () => {
    setCurrentDate(new Date());
  };
  
  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };
  
  const days = getDaysInMonth(currentDate);
  
  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="calendar-title-row">
          <h3>Calendar</h3>
          <button 
            className="today-btn"
            onClick={jumpToToday}
            aria-label="Jump to today"
          >
            Today
          </button>
        </div>
        <div className="calendar-nav">
          <button 
            className="nav-btn"
            onClick={() => navigateMonth('prev')}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="month-year">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button 
            className="nav-btn"
            onClick={() => navigateMonth('next')}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>
      
      <div className="calendar-grid">
        {/* Days of week header */}
        {daysOfWeek.map(day => (
          <div key={day} className="day-header">
            {day}
          </div>
        ))}
        
        {/* Calendar days - always 42 cells (6 weeks) */}
        {days.map((day, index) => (
          <div 
            key={index} 
            className={`calendar-day ${day ? 'has-day' : 'empty'} ${isToday(day) ? 'today' : ''}`}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
